import { useState } from "react"
import { useTranslation } from "react-i18next"
import i18n from "i18next"
import { Dialog, DialogProps } from "../ui/Dialog"
import { Button } from "../ui/Button"
import { OptionView } from "../ui/OptionView"
import { CatalogManager, StyleInfoRecord } from "@/lib/catalog"
import { pushDialog, resetDialogs } from "@/store/dialogs-state"
import { toast } from "react-hot-toast"
import { StyleService } from "@/store/style/style-service"
import { store } from "@/store/store"
import { styleReferencesAtom } from "@/store/style/style-state"
import { ConfirmDialog } from "./ConfirmDialog"
import { closeSession } from "@/routing/project-session"

export type StyleUpdatedOption = "update" | "publish_current"
export type StyleDeletedOption = "publish_current" | "select_new"
export const StyleSyncDialog = (
	props: DialogProps & {
		updatedStyle: StyleInfoRecord
		onOptionSelected: (option: StyleUpdatedOption) => void
	},
) => {
	const { t } = useTranslation("components")
	const [selected, setSelected] = useState<StyleUpdatedOption>("update")
	const [isLoading, setIsLoading] = useState(false)

	const confirm = async () => {
		switch (selected) {
			case "publish_current": {
				props.onOptionSelected(selected)
				break
			}
			case "update": {
				toast.loading(t("styleSyncDialog.updating"), {
					duration: Infinity,
					id: "style_update",
				})
				setIsLoading(true)
				try {
					const updatedReferenceResponses = await StyleService.getReferencesForStyle(
						props.updatedStyle.styleId,
					)

					const references = updatedReferenceResponses.map((response) =>
						StyleService.responseToReference(response),
					)

					await CatalogManager.replaceStyleReferences(references)
					store.set(styleReferencesAtom, references)
					toast.dismiss("style_update")
					toast.success(t("styleSyncDialog.updatedSuccessfully"))
					props.onOptionSelected("update")
					resetDialogs()
				} catch (error) {
					console.error("Error updating style references:", error)
					toast.dismiss("style_update")
					toast.error(t("styleSyncDialog.failedToUpdate"))
				} finally {
					setIsLoading(false)
				}
				break
			}
		}
	}

	return (
		<Dialog
			contentSize="md-h-auto"
			closeConfirm={confirmCancelStyleUpdatedCreation}
			{...props}
			bottomButtons={
				<div className="flex w-full flex-row items-center justify-end space-x-2">
					<Button
						trackingValue={selected}
						trackingLabel="continue"
						variant="primary"
						disabled={!selected || isLoading}
						onClick={confirm}
					>
						{t("common:action.continue")}
					</Button>
				</div>
			}
		>
			<div className="flex w-full flex-col space-y-4">
				<div className="text-start text-md font-bold">
					{t("styleSyncDialog.aiStyleUpdated")}
				</div>
				<div className="text-start text-sm">
					{t("styleSyncDialog.aiStyleUpdatedDescription")}
				</div>

				<div className="flex flex-col items-center space-y-2">
					<OptionView
						className="bg-transparent"
						id="update"
						title={t("styleSyncDialog.reprocessOption")}
						selected={selected === "update"}
						handleSelect={() => setSelected("update")}
					/>

					<OptionView
						className="bg-transparent"
						id="publish_current"
						title={t("styleSyncDialog.keepEditsOption")}
						selected={selected === "publish_current"}
						handleSelect={() => setSelected("publish_current")}
					/>
				</div>
			</div>
		</Dialog>
	)
}

export const StyleDeletedDialog = (
	props: DialogProps & {
		onOptionSelected: (option: StyleDeletedOption) => void
	},
) => {
	const { t } = useTranslation("components")
	const [selected, setSelected] = useState<StyleDeletedOption>("publish_current")

	const confirm = () => {
		switch (selected) {
			case "publish_current":
			case "select_new": {
				props.onOptionSelected(selected)
				break
			}
			default:
				break
		}
	}
	return (
		<Dialog
			contentSize="md-h-auto"
			closeConfirm={confirmCancelStyleUpdatedCreation}
			{...props}
			bottomButtons={
				<div className="flex w-full flex-row items-center justify-end space-x-2">
					<Button
						trackingValue={selected}
						trackingLabel="continue"
						variant="primary"
						disabled={!selected}
						onClick={confirm}
					>
						{t("common:action.continue")}
					</Button>
				</div>
			}
		>
			<div className="flex w-full flex-col space-y-4">
				<div className="text-start text-md font-bold">
					{t("styleSyncDialog.aiStyleDeleted")}
				</div>
				<div className="text-start text-sm">
					{t("styleSyncDialog.aiStyleDeletedDescription")}
				</div>

				<div className="flex flex-col items-center space-y-2">
					<OptionView
						className="bg-transparent"
						id="publish_current"
						title={t("styleSyncDialog.keepEditsOption")}
						selected={selected === "publish_current"}
						handleSelect={() => setSelected("publish_current")}
					/>
					<OptionView
						className="bg-transparent"
						id="select_new"
						title={t("styleSyncDialog.selectNewOption")}
						selected={selected === "select_new"}
						handleSelect={() => setSelected("select_new")}
					/>
				</div>
			</div>
		</Dialog>
	)
}

const confirmCancelStyleUpdatedCreation = () => {
	return new Promise<boolean>((resolve) => {
		pushDialog(ConfirmDialog, {
			trackingViewName: "cancel_style_update_confirmation",
			title: i18n.t("styleSyncDialog.cancelOpeningTitle", { ns: "components" }),
			message: i18n.t("styleSyncDialog.cancelOpeningMessage", { ns: "components" }),
			secondary: {
				title: i18n.t("styleSyncDialog.closeProject", { ns: "components" }),
				handler: async () => {
					resolve(true)
					await closeSession({
						saveChanges: false,
						forceClose: true,
						reason: "cancel_style_updated",
					})
				},
			},
			primary: {
				title: i18n.t("common:action.continue"),
				handler: () => {
					resolve(false)
				},
			},
		})
	})
}
